import DecimalLib from 'decimal.js'
import { CountryUtils } from './country.utils'

export const amountListFormatter = (value: string | undefined | null) => {
  if (value === undefined || value === null) {
    return ''
  }
  const pointGroups = `${value}`.match(/^([0-9]*).?([0-9]*)/)
  const beforePoint = pointGroups?.[1].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  const afterPoint = pointGroups?.[2] ?? `00`
  return `${beforePoint}.${afterPoint[0] ?? 0}${afterPoint[1] ?? 0}`
}

import type { CrudFilters } from '@refinedev/core'

export const onSearch = (data: unknown): CrudFilters => {
  const { searchValue } = data as { searchValue: string }
  const filters: CrudFilters = [
    {
      field: '',
      operator: 'contains' as const,
      value: searchValue,
    },
  ]
  return filters
}

export const getLabelByValue = (
  arr: { value: string; label: string }[],
  value: string
) => {
  return arr.find((item) => item.value === value)?.label || ''
}

export const getSymbolByName = (
  arr: { id: number; symbol: string; name: string }[],
  value: string
) => {
  return arr.find((item) => item.id === Number(value))?.symbol || ''
}

export const getNetWorkByName = (
  arr: { id: number; nativeCurrencySymbol: string; name: string }[],
  value: string
) => {
  return (
    arr.find((item) => item.id === Number(value))?.nativeCurrencySymbol || ''
  )
}

/** Format integer part with ASCII apostrophe as thousand separator: 1'000'000 */
function formatIntWithApostrophe(intStr: string): string {
  const isNeg = intStr.startsWith('-')
  const abs = isNeg ? intStr.slice(1) : intStr
  const formatted = abs.replace(/\B(?=(\d{3})+(?!\d))/g, "’")
  const result = isNeg ? '-' + formatted : formatted
  return result
}

export function formatNumber(
  num: number | string | null | undefined,
  decimal?: number,
  showPlus?: boolean
): string {
  // Handle null/undefined values
  if (num === null || num === undefined) {
    return '0'
  }

  const numeric = typeof num === 'number' ? num : Number(num)
  if (!isFinite(numeric)) return '0'

  let str = typeof num === 'number' ? num.toString() : num

  // Additional safety check for string type
  if (typeof str !== 'string' || !str) {
    return '0'
  }

  if (str.includes('e') || str.includes('E')) {
    str = scientificToDecimal(str)
  }

  const [intPart, decPart = ''] = str.split('.')

  if (decimal === undefined) {
    return Number(str).toLocaleString('en-US', {
      useGrouping: true,
      maximumFractionDigits: 30,
    })
  }

  const truncatedDecimal = decPart.slice(0, decimal).padEnd(decimal, '0')
  let formattedInt
  if (numeric > 0 && showPlus) {
    formattedInt = '+' + Number(intPart).toLocaleString('en-US')
  } else {
    formattedInt = Number(intPart).toLocaleString('en-US')
  }
  
  return `${formattedInt}.${truncatedDecimal}`
}

export function formatNumberV2(
  value: number | string | null | undefined,
  decimal?: number,
  showPlus?: boolean
): string {
  // Handle null/undefined values
  if (value === null || value === undefined) {
    return '0'
  }

  const numeric = typeof value === 'number' ? value : Number(value)
  if (!isFinite(numeric)) return '0'

  let str = typeof value === 'number' ? value.toString() : value

  // Additional safety check for string type
  if (typeof str !== 'string' || !str) {
    return '0'
  }

  if (str.includes('e') || str.includes('E')) {
    str = scientificToDecimal(str)
  }

  const [intPart, decPart = ''] = str.split('.')

  // If decimal is not provided, just format integer part and keep decimals as-is (no rounding)
  if (decimal === undefined) {
    const formattedInt = formatIntWithApostrophe(intPart)
    if (!decPart) return formattedInt
    return `${formattedInt}.${decPart}`
  }

  // When decimal is provided, use toFixed for rounding
  const roundedStr = numeric.toFixed(decimal)
  const [intPartRounded, decPartRounded] = roundedStr.split('.')

  let formattedInt = formatIntWithApostrophe(intPartRounded)
  if (Number(intPartRounded) > 0 && showPlus) {
    formattedInt = '+' + formattedInt
  }

  return decPartRounded !== undefined ? `${formattedInt}.${decPartRounded}` : formattedInt
}

export function formatNumberOnChangeInput(
  value: string | undefined | null,
  maxDecimals?: number
): string {
  if (value === undefined || value === null) return ''
  const original = String(value).trim()
  if (!original) return ''

  // Handle scientific notation paste (e.g. "9.99e+28")
  const sciMatch = original.match(/^([+-]?\d*\.?\d+)[eE]([+-]?\d+)$/)
  if (sciMatch) {
    const parsed = Number(original)
    if (isFinite(parsed)) {
      return formatNumberV2(parsed, maxDecimals)
    }
  }

  // only allow digits, dot, apostrophes (ASCII + Unicode right quote)
  let cleaned = original.replace(/[^\d.'\u2019]/g, '')

  // strip apostrophes before re-formatting
  cleaned = cleaned.replace(/['\u2019]/g, '')
  if (!cleaned) return ''

  // allow only one dot
  const firstDot = cleaned.indexOf('.')
  if (firstDot !== -1) {
    const before = cleaned.slice(0, firstDot)
    const after = cleaned.slice(firstDot + 1).replace(/\./g, '')
    cleaned = `${before}.${after}`
  }

  const [intPartRaw, decPartRaw] = cleaned.split('.')
  const formattedInt = formatIntWithApostrophe(intPartRaw || '0')

  // no decimals
  if (decPartRaw === undefined) {
    return formattedInt === '0' && intPartRaw === '' ? '' : formattedInt
  }

  // keep trailing dot while typing: "6." -> "6."
  if (original.endsWith('.')) {
    return `${formattedInt}.`
  }

  const dec =
    maxDecimals !== undefined ? decPartRaw.slice(0, maxDecimals) : decPartRaw
  return `${formattedInt}.${dec}`
}

/**
 * Parse value to Decimal for precise arithmetic. Accepts formatted strings (with ', ' etc).
 */
export function toDecimal(value: string | number | null | undefined): DecimalLib {
  if (value === null || value === undefined) return new DecimalLib(0)
  const cleaned = typeof value === 'string' ? parseFormattedNumberInput(value) : String(value)
  if (!cleaned) return new DecimalLib(0)
  const d = new DecimalLib(cleaned)
  return d.isFinite() ? d : new DecimalLib(0)
}

/**
 * Add values and truncate (no rounding) to `decimals` places, then format for display.
 */
export function addAndFormatTruncated(
  values: (string | number | null | undefined)[],
  decimals: number
): string {
  let sum = new DecimalLib(0)
  for (const v of values) {
    sum = sum.plus(toDecimal(v))
  }
  return sum.toFixed(decimals, DecimalLib.ROUND_DOWN)
}

/**
 * Subtract b from a and truncate (no rounding) to `decimals` places, then format for display.
 */
export function subAndFormatTruncated(
  a: string | number | null | undefined,
  b: string | number | null | undefined,
  decimals: number
): string {
  const result = toDecimal(a).minus(toDecimal(b))
  return result.toFixed(decimals, DecimalLib.ROUND_DOWN)
}

/**
 * Format value with truncation (no rounding) to `decimals` places.
 */
export function formatTruncated(
  value: string | number | null | undefined,
  decimals: number
): string {
  return toDecimal(value).toFixed(decimals, DecimalLib.ROUND_DOWN)
}

export function parseFormattedNumberInput(
  value: string | undefined | null
): string {
  if (value === undefined || value === null) return ''
  const s = String(value).trim()
  if (!s) return ''
  return s
    .replace(/\u2019/g, '')
    .replace(/\u0027/g, '')
    .replace(/\u202F/g, '')
    .replace(/,/g, '')
}

function scientificToDecimal(numStr: string): string {
  const num = Number(numStr)
  if (!isFinite(num)) return numStr

  const sign = num < 0 ? '-' : ''
  numStr = Math.abs(num).toString()

  if (!numStr.includes('e')) return sign + numStr

  const [base, exp] = numStr.split('e')
  const [intBase, decBase = ''] = base.split('.')
  const exponent = parseInt(exp, 10)

  if (exponent >= 0) {
    const combined = intBase + decBase.padEnd(exponent, '0')
    return sign + combined.padEnd(exponent + 1, '0')
  } else {
    const zeros = '0'.repeat(Math.abs(exponent) - 1)
    return sign + '0.' + zeros + intBase + decBase
  }
}

export function hasLeadingDecimalZeros(
  value: string,
  decimals: number
): boolean {
  const parts = value.split('.')
  if (parts.length !== 2) return false

  const decimalPart = parts[1]

  const match = decimalPart.match(/^0+/)
  const leadingZeros = match ? match[0].length : 0

  return leadingZeros === decimals
}

export const shortenAddress = (address: string) => {
  if (!address || address.length < 10) return address
  const first = address.slice(0, 4)
  const last = address.slice(-5)
  return `${first}....${last}`
}

export const padNumber = (num: number, targetLength?: number, preText?: string): string => {
  return `#${preText ?? ''}${String(num).padStart(targetLength || 4, '0')}`
}

export function roundTo(num: any, decimals = 6) {
  return (
    Math.round(Number(num) * Math.pow(10, decimals)) / Math.pow(10, decimals)
  )
}

export function roundToField(num: any, decimals = 6) {
  const n = Number(num)
  if (isNaN(n)) return null

  const rounded =
    Math.round(n * Math.pow(10, decimals)) / Math.pow(10, decimals)
  return rounded.toFixed(decimals) // Trả về string giữ đủ số chữ số thập phân
}

export const roundUp = (value: number, decimals = 2) => {
  const factor = Math.pow(10, decimals)
  return Math.ceil(value * factor) / factor
}

export const roundDown = (value: number, decimals = 2) => {
  const factor = Math.pow(10, decimals)
  return Math.floor(value * factor) / factor
}

export const fullNameCountry = (value: string) => {
  return value
    ? (CountryUtils[value as keyof typeof CountryUtils] ?? value)
    : ''
}
